import { luminance } from '@/lib/color/contrast.ts'

export const DUOTONE_FILTER_ID = 'brand-duotone'

function channels(hex: string): readonly [number, number, number] {
  const value = Number.parseInt(hex.slice(1), 16)
  return [((value >> 16) & 0xff) / 255, ((value >> 8) & 0xff) / 255, (value & 0xff) / 255]
}

/**
 * The reference's photo treatment as an SVG filter: shadows black, midtones
 * neutral graphite, highlights in the studio's colour.
 *
 * Built from the studio's accent at runtime, so one photograph serves every
 * studio in its own colour without an image pipeline. Rendered once, hidden;
 * images opt in with `filter: url(#brand-duotone)`.
 */
export function BrandDuotone({ brand }: { readonly brand: string }) {
  const valid = luminance(brand) !== null
  const [r, g, b] = valid ? channels(brand) : [0.27, 0.56, 1]
  // Six stops over luminance 0 → 1: black, three neutral greys, then into the
  // accent — only the brightest surfaces take the colour, as in the reference.
  const table = (accent: number, tint: number) =>
    `0 0.06 ${(0.13 + tint).toFixed(3)} ${(0.24 + tint).toFixed(3)} ${(accent * 0.6).toFixed(3)} ${accent.toFixed(3)}`

  return (
    <svg aria-hidden="true" focusable="false" width="0" height="0" className="absolute">
      <filter id={DUOTONE_FILTER_ID} colorInterpolationFilters="sRGB">
        <feColorMatrix type="saturate" values="0" />
        <feComponentTransfer>
          <feFuncR type="table" tableValues={table(r, 0)} />
          <feFuncG type="table" tableValues={table(g, 0.005)} />
          <feFuncB type="table" tableValues={table(b, 0.015)} />
        </feComponentTransfer>
      </filter>
    </svg>
  )
}
